import type { AnnotationAnchor, AnnotationLocator } from '../types/workflow.js';

export type AnchorResolution =
  | { status: 'exact' | 'reanchored'; startOffset: number; endOffset: number }
  | { status: 'stale' };

export interface AnnotationInterval {
  annotationId: string;
  startOffset: number;
  endOffset: number;
}

export interface AnnotationSegment {
  startOffset: number;
  endOffset: number;
  text: string;
  annotationIds: string[];
}

export function resolveLocator(patch: Record<string, unknown>, locator: AnnotationLocator): string | null {
  if (locator.scope === 'entity') {
    const value = patch[locator.field];
    return typeof value === 'string' ? value : null;
  }
  const values = patch[locator.scope === 'step' ? 'steps' : 'responses'];
  if (!Array.isArray(values)) return null;
  const value = values.find(
    (candidate): candidate is Record<string, unknown> =>
      isRecord(candidate) && isId(candidate.id) && String(candidate.id) === String(locator.id),
  );
  const fieldValue = value?.[locator.field];
  return typeof fieldValue === 'string' ? fieldValue : null;
}

export function resolveAnchor(
  anchor: AnnotationAnchor,
  currentPatch: Record<string, unknown>,
  currentPatchHash: string,
  storedPatchHash: string,
): AnchorResolution {
  const text = resolveLocator(currentPatch, anchor.locator);
  if (text === null || anchor.quote.length === 0) return { status: 'stale' };
  const candidates: Array<{ startOffset: number; score: number }> = [];
  let from = 0;
  while (from <= text.length - anchor.quote.length) {
    const startOffset = text.indexOf(anchor.quote, from);
    if (startOffset < 0) break;
    candidates.push({
      startOffset,
      score:
        Number(text.slice(Math.max(0, startOffset - anchor.prefix.length), startOffset) === anchor.prefix) +
        Number(text.slice(startOffset + anchor.quote.length, startOffset + anchor.quote.length + anchor.suffix.length) === anchor.suffix),
    });
    from = startOffset + 1;
  }
  if (!candidates.length) return { status: 'stale' };
  const score = Math.max(...candidates.map((candidate) => candidate.score));
  const best = candidates.filter((candidate) => candidate.score === score);
  if (best.length !== 1) return { status: 'stale' };
  const startOffset = best[0]!.startOffset;
  return {
    status:
      startOffset === anchor.startOffset &&
      text.slice(anchor.startOffset, anchor.endOffset) === anchor.quote &&
      currentPatchHash === storedPatchHash
        ? 'exact'
        : 'reanchored',
    startOffset,
    endOffset: startOffset + anchor.quote.length,
  };
}

export function rangeToSourceOffsets(root: HTMLElement, range: Range): { startOffset: number; endOffset: number } | null {
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const beforeStart = document.createRange();
  beforeStart.selectNodeContents(root);
  beforeStart.setEnd(range.startContainer, range.startOffset);
  const beforeEnd = document.createRange();
  beforeEnd.selectNodeContents(root);
  beforeEnd.setEnd(range.endContainer, range.endOffset);
  const startOffset = beforeStart.toString().length;
  const endOffset = beforeEnd.toString().length;
  return startOffset < endOffset ? { startOffset, endOffset } : null;
}

export function segmentAnnotationIntervals(text: string, intervals: AnnotationInterval[]): AnnotationSegment[] {
  const valid = intervals.filter(
    ({ startOffset, endOffset }) =>
      Number.isInteger(startOffset) && Number.isInteger(endOffset) && startOffset >= 0 && endOffset > startOffset && endOffset <= text.length,
  );
  const boundaries = [...new Set([0, text.length, ...valid.flatMap(({ startOffset, endOffset }) => [startOffset, endOffset])])]
    .sort((left, right) => left - right);
  const segments: AnnotationSegment[] = [];
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const startOffset = boundaries[index]!;
    const endOffset = boundaries[index + 1]!;
    if (startOffset === endOffset) continue;
    segments.push({
      startOffset,
      endOffset,
      text: text.slice(startOffset, endOffset),
      annotationIds: valid
        .filter((interval) => interval.startOffset < endOffset && interval.endOffset > startOffset)
        .map((interval) => interval.annotationId),
    });
  }
  return segments;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isId(value: unknown): value is string | number {
  return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));
}
